import React from 'react';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import { charityConfig } from '../../config/charity';
import InlineTranslationEdit from '../InlineTranslationEdit';
import { useContent } from '../../contexts/ContentContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';

export default function GrowingImpactCharity({ weekNumber = 1, totalRaised = 0, editMode = false, handleTranslationUpdate }) {
  const { c } = useContent();
  const { siteDetails } = useSiteConfig() || {};
  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl;
  const brandName = siteDetails?.siteName || charityConfig.name;
  const progressPercentage = Math.min((weekNumber / 52) * 100, 100);

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-pink-50 to-white dark:from-purple-950 dark:via-pink-950 dark:to-gray-900 rounded-2xl shadow-lg border border-purple-200 dark:border-purple-800">
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-yellow-400/20 to-pink-400/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-400/20 to-fuchsia-400/20 rounded-full blur-2xl"></div>

      <div className="relative p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg shadow-md">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-gray-900 dark:text-white"><InlineTranslationEdit translationKey="CharityGrowingImpactTitle" value={c('CharityGrowingImpactTitle', 'Growing Impact')} fallback="Growing Impact" onUpdate={handleTranslationUpdate} editMode={editMode} /></h3>
          </div>
          <div className="flex items-center gap-1 px-3 py-1 bg-purple-100 dark:bg-purple-900/40 rounded-full">
            <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm font-semibold text-purple-700 dark:text-purple-300"><InlineTranslationEdit translationKey="CharityGrowingImpactWeek" value={c('CharityGrowingImpactWeek', 'Week')} fallback="Week" onUpdate={handleTranslationUpdate} editMode={editMode} />{' '}{weekNumber}</span>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400"><InlineTranslationEdit translationKey="CharityGrowingImpactProgress" value={c('CharityGrowingImpactProgress', '52-Week Community Impact')} fallback="52-Week Community Impact" onUpdate={handleTranslationUpdate} editMode={editMode} /></span>
            <span className="text-sm font-bold text-purple-600 dark:text-purple-400">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-400 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progressPercentage}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 animate-shimmer"></div>
            </div>
          </div>
          <p className="mt-2 text-xs text-center text-gray-500 dark:text-gray-400">
            <InlineTranslationEdit translationKey="CharityGrowingImpactSubtext" value={c('CharityGrowingImpactSubtext', 'The longer you play, the more we give')} fallback="The longer you play, the more we give" onUpdate={handleTranslationUpdate} editMode={editMode} />
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/40 dark:to-pink-900/40 rounded-xl p-4 mb-4 border border-purple-200 dark:border-purple-700">
          <p className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide mb-1 text-center"><InlineTranslationEdit translationKey="CharityGrowingImpactTotal" value={c('CharityGrowingImpactTotal', 'Total Community Impact')} fallback="Total Community Impact" onUpdate={handleTranslationUpdate} editMode={editMode} /></p>
          <p className="text-3xl font-black text-center bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
            ${totalRaised.toLocaleString()}
          </p>
        </div>

        <div className="flex items-center gap-4 p-4 bg-white/80 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="relative">
            <img
              src={brandLogo}
              alt={brandName}
              className="w-16 h-16 object-contain rounded-lg bg-white p-1 shadow-md"
            />
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-md">
              <Heart className="w-3 h-3 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-gray-900 dark:text-white mb-1">{brandName}</h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              {charityConfig.tagline}
            </p>
          </div>
        </div>

        <div className="mt-4 text-center">
          <a
            href={charityConfig.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors group"
          >
            <InlineTranslationEdit translationKey="CharityGrowingImpactCTA" value={c('CharityGrowingImpactCTA', 'See our impact story')} fallback="See our impact story" onUpdate={handleTranslationUpdate} editMode={editMode} />
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
}
